/**
@license
Copyright 2018 The Advanced REST client authors <arc@mulesoft.com>
Licensed under the Apache License, Version 2.0 (the "License"); you may not
use this file except in compliance with the License. You may obtain a copy of
the License at
http://www.apache.org/licenses/LICENSE-2.0
Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS, WITHOUT
WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied. See the
License for the specific language governing permissions and limitations under
the License.
*/
import { LitElement } from 'lit-element';

/**
 * `<api-console-ext-comm>` is an element that support communication with
 * the api-console-extension.
 *
 * If the extension is installed then it will intercept the `api-request`
 * and cancel it.
 *
 * Data from the event are passed to the extension and the request is to
 * be executed from within the extension.
 *
 * @customElement
 * @memberof ApiElements
 */
class ApiConsoleExtComm extends LitElement {
  createRenderRoot() {
    return null;
  }

  static get properties() {
    return {
      /**
       * If true then the API console extension has been detected.
       */
      hasExtension: { type: Boolean },
      /**
       * An event handler for the API console events (request, abort,
       * token request etc). By default it uses body but it should be
       * the console if possible or the request panel.
       */
      eventTarget: { type: Object },
      /**
       * List of active requests sent to the extension.
       * The key is request ID (generated by the console) and the value
       * is the request data.
       */
      _activeRequests: { type: Object }
    };
  }

  get hasExtension() {
    return this._hasExtension;
  }

  get _hasExtension() {
    return this.__hasExtension;
  }

  set _hasExtension(value) {
    const old = this.__hasExtension;
    /* istanbul ignore if */
    if (old === value) {
      return;
    }
    this.__hasExtension = value;
    this.dispatchEvent(new CustomEvent('hasextension-changed', {
      detail: { value }
    }));
  }

  get eventTarget() {
    return this._eventTarget;
  }

  set eventTarget(value) {
    const old = this._eventTarget;
    /* istanbul ignore if */
    if (old === value) {
      return;
    }
    this._eventTarget = value;
    this._eventTargetChanged(value, old);
  }

  constructor() {
    super();
    this._hasExtension = false;
    this.eventTarget = document.body;
    this._activeRequests = {};
    this._messsageHandler = this._messsageHandler.bind(this);
    this._requestHandler = this._requestHandler.bind(this);
    this._oauthTokenHandler = this._oauthTokenHandler.bind(this);
    this._abortHandler = this._abortHandler.bind(this);
  }

  connectedCallback() {
    if (super.connectedCallback) {
      super.connectedCallback();
    }
    window.addEventListener('message', this._messsageHandler);
    this._notifyExtension();
    this.setAttribute('aria-hidden', 'true');
  }

  disconnectedCallback() {
    if (super.disconnectedCallback) {
      super.disconnectedCallback();
    }
    window.removeEventListener('message', this._messsageHandler);
    if (this.eventTarget) {
      this._unobserverConsoleEvents(this.eventTarget);
      this.eventTarget = undefined;
    }
  }
  /**
   * Posts message on a window object to request an event from the
   * extension if it is installed.
   */
  _notifyExtension() {
    window.postMessage({
      payload: 'api-console-extension-installed'
    }, location.origin);
  }
  /**
   * Handler for event target change. Removes listener from old
   * hasndelr and sets up API console event listeners on the node.
   * @param {Node} et New event target
   * @param {Node} old
   */
  _eventTargetChanged(et, old) {
    if (old) {
      this._unobserverConsoleEvents(old);
    }
    if (et) {
      this._observerConsoleEvents(et);
    }
  }
  /**
   * Sets up API console event listeners on the target node.
   * @param {Node} node Target for the events
   */
  _observerConsoleEvents(node) {
    node.addEventListener('api-request', this._requestHandler);
    node.addEventListener('abort-api-request', this._abortHandler);
    node.addEventListener('oauth2-token-requested', this._oauthTokenHandler);
  }
  /**
   * Removes API console event listeners from the target node.
   * @param {Node} node Target for the events
   */
  _unobserverConsoleEvents(node) {
    node.removeEventListener('api-request', this._requestHandler);
    node.removeEventListener('abort-api-request', this._abortHandler);
    node.removeEventListener('oauth2-token-requested', this._oauthTokenHandler);
  }
  /**
   * A handler for the message event dispatched on window object.
   * This is used in communication with an extension.
   *
   * @param {MessageEvent} e
   */
  _messsageHandler(e) {
    if (e.source !== window || !e.data) {
      return;
    }
    const data = e.data;
    if (!data['api-console-extension']) {
      return;
    }
    switch (data['api-console-payload']) {
      case 'init':
        this._extensionDetected();
        break;
      case 'api-console-response':
        this._responseReady(data['api-console-data']);
        break;
      case 'api-console-oauth2-token-response':
        this._oauthTokenReady(data['api-console-data']);
        break;
    }
  }
  /**
   * A handler for API console request event
   *
   * @param {CustomEvent} e
   */
  _requestHandler(e) {
    if (!this.hasExtension) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
    const detail = {
      headers: e.detail.headers,
      id: e.detail.id,
      method: e.detail.method,
      payload: e.detail.payload,
      url: e.detail.url
    };
    this._activeRequests[e.detail.id] = detail;
    window.postMessage({
      payload: 'api-console-request',
      detail
    }, location.origin);
  }
  /**
   * A handler for API console abort request handler.
   *
   * @param {CustomEvent} e
   */
  _abortHandler(e) {
    if (!this.hasExtension) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
    // Without the entry it won't report the response back.
    delete this._activeRequests[e.detail.id];
  }
  /**
   * A handler for API console OAuth2 toekn request handler.
   *
   * @param {CustomEvent} e
   */
  _oauthTokenHandler(e) {
    if (!this.hasExtension) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
    window.postMessage({
      payload: 'api-console-oauth2',
      detail: e.detail
    }, location.origin);
  }
  /**
   * Called when the api-console-extension is detected.
   */
  _extensionDetected() {
    this._hasExtension = true;
    this.dispatchEvent(new CustomEvent('api-console-extension-installed', {
      bubbles: true,
      composed: true
    }));
  }
  /**
   * A handler for the response notified by the extension.
   * @param {Object} data
   */
  _responseReady(data) {
    if (!this.hasExtension) {
      return;
    }
    const response = data.data;
    const request = this._activeRequests[response.id];
    if (!request) {
      // operation has been aborted
      return;
    }
    delete this._activeRequests[response.id];
    response.request = request;
    this.dispatchEvent(new CustomEvent('api-response', {
      bubbles: true,
      composed: true,
      detail: response
    }));
  }
  /**
   * Handler for OAuth token response.
   * @param {Object} data
   */
  _oauthTokenReady(data) {
    if (!data) {
      this.dispatchEvent(new CustomEvent('oauth2-error', {
        bubbles: true,
        composed: true,
        detail: {
          message: 'No response has been recorded.',
          code: 'no_response'
        }
      }));
      return;
    }
    if (data.error) {
      const message = data.message && typeof data.message === 'string' ?
        data.message : 'No response has been recorded.';
      const code = data.code && typeof data.code === 'string' ?
        data.code : 'unknown_error';
      this.dispatchEvent(new CustomEvent('oauth2-error', {
        bubbles: true,
        composed: true,
        detail: {
          message: message,
          code: code
        }
      }));
      return;
    }
    const state = data.state && typeof data.state === 'string' ?
      data.state : undefined;
    const accessToken = data.accessToken && typeof data.accessToken === 'string' ?
      data.accessToken : undefined;
    const tokenType = data.tokenType && typeof data.tokenType === 'string' ?
      data.tokenType : undefined;
    const tokenTime = data.tokenTime && typeof data.tokenTime === 'number' ?
      data.tokenTime : undefined;
    const expiresIn = data.expiresIn && (typeof data.expiresIn === 'number' ||
      typeof data.expiresIn === 'string') ? Number(data.expiresIn) : undefined;
    const scope = data.scope && typeof data.scope === 'string' ?
      data.scope : undefined;
    this.dispatchEvent(new CustomEvent('oauth2-token-response', {
      bubbles: true,
      composed: true,
      detail: {
        state,
        accessToken,
        tokenType,
        tokenTime,
        expiresIn,
        scope
      }
    }));
  }
  /**
   * Called when the API console extension has been detected,
   *
   * @event api-console-extension-installed
   */
}
window.customElements.define('api-console-ext-comm', ApiConsoleExtComm);
